import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"

import { parseResultJson } from "../helpers/parseResultJson"

/** Gaps shorter than this, in seconds, are not shown as a response latency. */
const MIN_LATENCY_THRESHOLD = 0.1

interface LatencyStatistics {
  median_latency?: number | null
  min_latency?: number | null
  max_latency?: number | null
  std_dev?: number | null
  average_latency?: number | null
  mean_latency?: number | null
  distribution_metrics?: {
    percentiles?: {
      p90?: number | null
    }
  }
  outlier_analysis?: {
    metadata?: {
      outlier_count?: number
    }
  }
}

interface RawLatencyMeasurement extends Record<string, unknown> {
  latency?: number
  silence_start?: number
  silence_end?: number
  to_speaker?: string
}

export interface LatencyResultJson {
  latency_analysis?: {
    statistics?: LatencyStatistics
    latencies?: RawLatencyMeasurement[]
  }
  filter_role?: string
}

/**
 * One silence before a response, in seconds on the audio clock: `start` is
 * where the previous speaker stopped, `end` is where `toSpeaker` began.
 */
export interface LatencyGap {
  start: number
  end: number
  latencySeconds: number
  toSpeaker: string | undefined
}

/**
 * The speaker whose responses a latency output measured. A legacy output names
 * it in `result_json`; a typed one names it in `result.raw_values`, often next
 * to an empty `result_json`, so neither can be read alone.
 */
export function resolveLatencyFilterRole(
  mo: RunMetricOutput | MetricOutputDisplaySource | undefined,
): string | undefined {
  if (!mo) {
    return undefined
  }
  const legacyRole = parseResultJson<LatencyResultJson>(mo)?.filter_role
  if (typeof legacyRole === "string") {
    return legacyRole
  }
  const rawValues: unknown = mo.result?.raw_values
  if (rawValues != null && typeof rawValues === "object") {
    const rawRole = (rawValues as LatencyResultJson).filter_role
    return typeof rawRole === "string" ? rawRole : undefined
  }
  return undefined
}

/**
 * The response gaps a latency output measured, limited to responses by the
 * metric's own role and to gaps long enough to show. Reads
 * `subvalues_by_timestamp` and falls back to the legacy
 * `result_json.latency_analysis.latencies`.
 */
export function resolveLatencyGaps(
  mo: RunMetricOutput | MetricOutputDisplaySource,
): LatencyGap[] {
  const legacy = parseResultJson<LatencyResultJson>(mo)
  const filterRole = resolveLatencyFilterRole(mo)

  const subvalues: SubvalueByTimestamp[] = mo.subvalues_by_timestamp ?? []
  if (subvalues.length > 0) {
    return subvalues
      .filter(
        (sv) =>
          (filterRole == null || sv.metadata?.to_speaker === filterRole) &&
          (sv.float_value ?? 0) >= MIN_LATENCY_THRESHOLD,
      )
      .map((sv) => ({
        start: sv.start_offset,
        end: sv.end_offset,
        latencySeconds: sv.float_value ?? 0,
        toSpeaker:
          typeof sv.metadata?.to_speaker === "string"
            ? sv.metadata.to_speaker
            : filterRole,
      }))
  }

  return (legacy?.latency_analysis?.latencies ?? [])
    .map((m) => ({
      start: m.silence_start || 0,
      end: m.silence_end || 0,
      latencySeconds: m.latency || 0,
      toSpeaker: m.to_speaker || "unknown",
    }))
    .filter(
      (gap) =>
        gap.toSpeaker === filterRole &&
        gap.latencySeconds >= MIN_LATENCY_THRESHOLD,
    )
}
